import React, { useState, useCallback, useEffect } from 'react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { PracticeInfoForm } from './components/PracticeInfoForm';
import { ContentPlanDisplay } from './components/ContentPlanDisplay';
import { generateContentPlan, generateSinglePost, fetchPostPlannerInfo } from './services/openaiService';
import { fetchPastPostsFromSalesforce } from './services/salesforceUploadService';
import { ErrorDisplay } from './components/ErrorDisplay';
import { Hero } from './components/Hero';
import { Header } from './components/Header';
import { calculatePostDate } from './utils/dateUtils';
import './PostPlanner.css';


// Helper function to convert Milestone[] to string format for openaiService
const milestonesToString = (milestones) => {
  return milestones.map(m => {
    const typeLabel = m.type === 'birthday' ? 'Birthday' : 'Work Anniversary';
    return `${m.name} ${typeLabel} - ${m.month} ${m.day}`;
  }).join('\n');
};

// Draft persistence — saves form state and the generated plan to localStorage
// so a refresh, auth bounce, or accidental tab close doesn't wipe the user's work.
const DRAFT_STORAGE_KEY = 'gargle:postPlannerDraft:v1';
const DRAFT_SAVE_DEBOUNCE_MS = 500;

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn('Failed to load post planner draft:', err);
    return null;
  }
};

const App = () => {
  const context = useAuthContext();
  // Hydrate from saved draft once on mount. Lazy initializer keeps the JSON parse off subsequent renders.
  const [initialDraft] = useState(() => loadDraft() || {});
  const [practiceName, setPracticeName] = useState(initialDraft.practiceName || '');
  const [practiceUrl, setPracticeUrl] = useState(initialDraft.practiceUrl || '');
  const [practicePhone, setPracticePhone] = useState(initialDraft.practicePhone || '');
  const [practiceLocation, setPracticeLocation] = useState(initialDraft.practiceLocation || '');
  const [startDate, setStartDate] = useState(initialDraft.startDate || '');
  const [postSchedule, setPostSchedule] = useState(initialDraft.postSchedule || 'MO-WE');
  const [postsPerWeek, setPostsPerWeek] = useState(initialDraft.postsPerWeek ?? 2);
  const [specialInstructions, setSpecialInstructions] = useState(initialDraft.specialInstructions || '');
  const [milestones, setMilestones] = useState(initialDraft.milestones || []);
  const [contentPlan, setContentPlan] = useState(initialDraft.contentPlan || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(initialDraft.savedAt || null);
  // Which OpenAI model the proxy is using — shown in the UI so the model can be verified at a glance.
  const [generationModel, setGenerationModel] = useState(null);

  // State for tracking multiple regenerating posts
  const [regeneratingPosts, setRegeneratingPosts] = useState(new Set());
  // State to hold the text content from Salesforce past posts (fetched automatically when account is selected)
  const [pastPostsContent, setPastPostsContent] = useState();
  // Cache research results to avoid re-fetching
  const [cachedResearch, setCachedResearch] = useState(null);
  // Track selected Salesforce account for upload functionality
  const [selectedSalesforceAccount, setSelectedSalesforceAccount] = useState(initialDraft.selectedSalesforceAccount || null);
  // Track additional Salesforce accounts for multi-location upload
  const [additionalAccounts, setAdditionalAccounts] = useState(initialDraft.additionalAccounts || []);

  // Clear cached research if practice URL changes
  useEffect(() => {
    if (cachedResearch && cachedResearch.url !== practiceUrl) {
      setCachedResearch(null);
    }
  }, [practiceUrl, cachedResearch]);

  // Auto-save draft to localStorage so a refresh/auth-bounce/tab-close doesn't wipe work
  useEffect(() => {
    const handle = setTimeout(() => {
      try {
        const savedAt = Date.now();
        const draft = {
          practiceName,
          practiceUrl,
          practicePhone,
          practiceLocation,
          startDate,
          postSchedule,
          postsPerWeek,
          specialInstructions,
          milestones,
          contentPlan,
          selectedSalesforceAccount,
          additionalAccounts,
          savedAt,
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
        setLastSavedAt(savedAt);
      } catch (err) {
        console.warn('Failed to save post planner draft:', err);
      }
    }, DRAFT_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [practiceName, practiceUrl, practicePhone, practiceLocation, startDate, postSchedule, postsPerWeek, specialInstructions, milestones, contentPlan, selectedSalesforceAccount, additionalAccounts]);

  // Backstop: warn before unload if there's a generated plan or meaningful work in progress.
  useEffect(() => {
    const hasWork = !!contentPlan || !!practiceName || !!practiceUrl || !!specialInstructions || milestones.length > 0;
    if (!hasWork) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [contentPlan, practiceName, practiceUrl, specialInstructions, milestones]);

  const handleClearDraft = useCallback(() => {
    if (!window.confirm('Clear your saved post planner draft? This erases the current form inputs and any generated plan.')) return;
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (err) {
      console.warn('Failed to clear post planner draft:', err);
    }
    setPracticeName('');
    setPracticeUrl('');
    setPracticePhone('');
    setPracticeLocation('');
    setStartDate('');
    setPostSchedule('MO-WE');
    setPostsPerWeek(2);
    setSpecialInstructions('');
    setMilestones([]);
    setContentPlan(null);
    setSelectedSalesforceAccount(null);
    setAdditionalAccounts([]);
    setError(null);
    setLastSavedAt(null);
  }, []);

  // Fetch past posts from Salesforce when account is selected
  useEffect(() => {
    const fetchPastPosts = async () => {
      if (selectedSalesforceAccount?.Id) {
        try {
          const pastPosts = await fetchPastPostsFromSalesforce(selectedSalesforceAccount.Id, context);
          setPastPostsContent(pastPosts);
        } catch (error) {
          console.error('Error fetching past posts from Salesforce:', error);
          // Don't set error state - this is optional functionality
        }
      } else {
        setPastPostsContent(undefined);
      }
    };

    fetchPastPosts();
  }, [selectedSalesforceAccount, context]);

  // Ask the proxy which model it is using, so the UI can display it. Best-effort:
  // if the proxy is unreachable we simply omit the badge.
  useEffect(() => {
    let cancelled = false;
    fetchPostPlannerInfo()
      .then(info => {
        if (!cancelled) setGenerationModel(info.generationModel || null);
      })
      .catch(err => {
        console.warn('Could not load Post Planner model info:', err);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!practiceName || !practiceUrl || !startDate) {
      setError('Please fill out all fields, including the start date, before generating the plan.');
      return;
    }

    // Validate that the correct number of days are selected
    const selectedDays = postSchedule ? postSchedule.split('-') : [];
    if (selectedDays.length !== postsPerWeek) {
      setError(`Please select exactly ${postsPerWeek} days of the week for posting.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setContentPlan(null);

    try {
      const plan = await generateContentPlan(
        practiceName,
        practiceUrl,
        startDate,
        postSchedule,
        postsPerWeek,
        pastPostsContent, // Use past posts from Salesforce
        undefined, // No onboarding content
        specialInstructions,
        practicePhone,
        practiceLocation,
        milestonesToString(milestones),
        cachedResearch,
        setCachedResearch,
        selectedSalesforceAccount,
        context
      );
      setContentPlan(plan);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to generate content plan. ${errorMessage}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [practiceName, practiceUrl, practicePhone, practiceLocation, startDate, postSchedule, postsPerWeek, pastPostsContent, specialInstructions, milestones, cachedResearch, selectedSalesforceAccount, context]);

  const handleContentPlanChange = useCallback((weekIndex, postIndex, field, value) => {
    setContentPlan(currentPlan => {
      if (!currentPlan) return null;
      const newPlan = JSON.parse(JSON.stringify(currentPlan));
      newPlan[weekIndex].posts[postIndex][field] = value;
      return newPlan;
    });
  }, []);

  const handleRegeneratePost = useCallback(async (weekIndex, postIndex, instructions) => {
    if (!contentPlan) return;

    const postKey = `${weekIndex}-${postIndex}`;

    // Add this post to the regenerating set
    setRegeneratingPosts(prev => new Set(prev).add(postKey));
    setError(null);

    try {
      const postDate = calculatePostDate(startDate, weekIndex, postIndex, postSchedule);

      const newPost = await generateSinglePost(
        practiceName,
        practiceUrl,
        contentPlan,
        { weekIndex, postIndex },
        postDate.toISOString().split('T')[0], // Pass date as YYYY-MM-DD
        instructions,
        undefined, // No onboarding content
        pastPostsContent,
        specialInstructions,
        practicePhone,
        practiceLocation,
        cachedResearch,
        setCachedResearch,
        context
      );

      setContentPlan(currentPlan => {
        if (!currentPlan) return null;
        const newPlan = JSON.parse(JSON.stringify(currentPlan));
        newPlan[weekIndex].posts[postIndex] = newPost;
        return newPlan;
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(`Failed to regenerate post. ${errorMessage}`);
      console.error(err);
    } finally {
      // Remove this post from the regenerating set
      setRegeneratingPosts(prev => {
        const next = new Set(prev);
        next.delete(postKey);
        return next;
      });
    }
  }, [practiceName, practiceUrl, startDate, postSchedule, contentPlan, pastPostsContent, cachedResearch, specialInstructions, practicePhone, practiceLocation, context]);


  return (
    <div className="post-planner-shell bg-gray-50 min-h-screen text-gray-800 font-serif">
      <Header />
      <main className="post-planner-main">
        <div style={{ width: '100%' }}>
          <Hero />

          {generationModel && (
            <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '9999px',
                  padding: '0.25rem 0.7rem',
                  fontFamily: 'inherit',
                }}
              >
                AI model: {generationModel}
              </span>
            </div>
          )}

          <div className="post-planner-form-container mt-12">
            {(lastSavedAt || contentPlan) && (
              <div className="post-planner-draft-status">
                <span className="post-planner-draft-status-text">
                  {lastSavedAt
                    ? `Draft auto-saved at ${new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                    : 'Auto-save enabled'}
                </span>
                <button
                  type="button"
                  onClick={handleClearDraft}
                  className="post-planner-clear-draft-btn"
                >
                  Clear draft
                </button>
              </div>
            )}
            <PracticeInfoForm
              practiceName={practiceName}
              setPracticeName={setPracticeName}
              practiceUrl={practiceUrl}
              setPracticeUrl={setPracticeUrl}
              practicePhone={practicePhone}
              setPracticePhone={setPracticePhone}
              practiceLocation={practiceLocation}
              setPracticeLocation={setPracticeLocation}
              startDate={startDate}
              setStartDate={setStartDate}
              postSchedule={postSchedule}
              setPostSchedule={setPostSchedule}
              postsPerWeek={postsPerWeek}
              setPostsPerWeek={setPostsPerWeek}
              specialInstructions={specialInstructions}
              setSpecialInstructions={setSpecialInstructions}
              milestones={milestones}
              setMilestones={setMilestones}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              selectedSalesforceAccount={selectedSalesforceAccount}
              setSelectedSalesforceAccount={setSelectedSalesforceAccount}
            />

            {error && <div className="mt-6"><ErrorDisplay message={error} /></div>}

            {contentPlan && !isLoading && !error && (
              <div className="post-planner-content-container mt-12 pt-12 border-t border-gray-200">
                <ContentPlanDisplay
                  contentPlan={contentPlan}
                  practiceName={practiceName}
                  postSchedule={postSchedule}
                  postsPerWeek={postsPerWeek}
                  startDate={startDate}
                  onContentChange={handleContentPlanChange}
                  onRegeneratePost={handleRegeneratePost}
                  regeneratingPosts={regeneratingPosts}
                  selectedSalesforceAccount={selectedSalesforceAccount}
                  additionalAccounts={additionalAccounts}
                  setAdditionalAccounts={setAdditionalAccounts}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="text-center py-6 text-sm text-gray-500">
        <p>&copy; {new Date().getFullYear()} Gargle. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default App;
